import { formatAvgPerDay, reportingCalendarDayCount } from "../../lib/dashboardTime.js";
import { formatGbpPence } from "../../lib/formatMoney.js";
import type { DashboardTabHandler, FilterState, OpenRawEventsHandler } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import { FunnelChart } from "../charts/FunnelChart.js";

const FUNNEL_CRITERIA = `Counts from device_events:

0) Interaction stints — distinct non-empty properties.interaction_session_id (dashboard filters including Game; same rollup as Interaction sessions tab). Avg. /day = stint count ÷ inclusive reporting-calendar days in the filter range.
1–4) Payment steps — date + venue + device only (no Game filter; mobile pay rows often lack game_route / interaction_session_id):
   1) popup_open where popup = payment — count(distinct interaction_session_id) (* one per ISID; rows without ISID excluded)
   2) Mobile Pageview — count(distinct play session id) of screen_interacted mobile_payment page_opened (legacy: screen_viewed path /pay%) where the play session was still live: created ≤30 min before the open and has a play_session_payments row (pay page showed a price). Raw event count (incl. stale-tab reloads) is in the step tooltip.
   3) Payment Attempted — same live-session rule for express_checkout_pressed (distinct play session id; raw count in tooltip).
   4) Payment Confirmed bar — play-session grain (same CTE as Paid session explorer): light green = distinct play session id with table payment_succeeded; dark green = shell telemetry gap (payment_confirmed with no matching payment_succeeded by same play session id OR same device within −2 min…+10 min). Combined count = explorer total. Do not subtract shell-gap from distinct checkout_id (KPI “Payments confirmed” / Gross Rev. use checkout / amount grain separately). Gross Rev. = sum of effective payment_confirmed amounts (prefer play_session_payments via checkout_id / play session id, else event amount_pence); shell-gap £ is a subset caveat.

* Table QR Code is unique per interaction_session_id (ISID), not raw popup_open row count.

Bar width: step count / first step (Interaction Stints = 100%).

Step click: Interaction Stints → Interaction sessions tab; other steps → Raw Events with the event_name / Properties filter from the API query (raw events, not the live-session distinct count). Dark green segment click → Raw Events → payment_confirmed (all confirmed rows; gap-only cannot be expressed as one filter).`;

interface PaymentFlowFunnelCardProps {
  funnelDisplay: number[];
  grossRevPence?: number;
  /** Registered play sessions (table payment_succeeded) — light-green segment. */
  registeredPayments?: number;
  /** Shell-gap play sessions — dark-green segment. */
  unregisteredPayments?: number;
  /** Amount (pence) of shell-gap confirms — subset of grossRevPence, shown as a caveat. */
  unregisteredRevPence?: number;
  /** Raw page_opened (+ legacy) events — shown in Mobile Pageview tooltip. */
  mobilePageviewEvents?: number;
  /** Raw express_checkout_pressed events — shown in Payment Attempted tooltip. */
  paymentAttemptedEvents?: number;
  filters: FilterState;
  reportingTimezone: string;
  onOpenRawEvents: OpenRawEventsHandler;
  onOpenSessions: DashboardTabHandler;
  compact?: boolean;
  showCriteria?: boolean;
}

export function PaymentFlowFunnelCard({
  funnelDisplay,
  grossRevPence = 0,
  registeredPayments,
  unregisteredPayments = 0,
  unregisteredRevPence = 0,
  mobilePageviewEvents,
  paymentAttemptedEvents,
  filters,
  reportingTimezone,
  onOpenRawEvents,
  onOpenSessions,
  compact = false,
  showCriteria = true,
}: PaymentFlowFunnelCardProps) {
  const stintCount = funnelDisplay[0] ?? 0;
  const reportingDays = reportingCalendarDayCount(filters.from, filters.to, reportingTimezone);
  const avgStintsPerDay = formatAvgPerDay(stintCount, reportingDays);
  const hasShellGap = unregisteredPayments > 0;
  const paymentConfirmedTotal = funnelDisplay[4] ?? 0;
  const registeredConfirmed = hasShellGap
    ? (registeredPayments ?? Math.max(0, paymentConfirmedTotal - unregisteredPayments))
    : paymentConfirmedTotal;

  return (
    <ChartCard
      compact={compact}
      title="Payment Flow Funnel"
      criteria={showCriteria ? FUNNEL_CRITERIA : undefined}
      note="Steps: interaction stints → table payment popup* (one per ISID) → live mobile pay opens (distinct play session) → live checkout presses → payment confirmed (platform). % vs Interaction Stints. Payment steps ignore the Game dropdown."
    >
      <FunnelChart
        steps={[
          {
            label: "Interaction Stints",
            value: stintCount,
            color: "#8b7cf6",
            summary: "People trying the table",
            trailingNote: { label: "Avg. /day", value: avgStintsPerDay },
          },
          {
            label: "Table QR Code*",
            value: funnelDisplay[1] ?? 0,
            color: "#5aa2ff",
            summary: "Pressed play, saw QR Code",
          },
          {
            label: "Mobile Pageview",
            value: funnelDisplay[2] ?? 0,
            color: "#f472b6",
            summary: "Opened Pay Page on Mobile",
          },
          {
            label: "Payment Attempted",
            value: funnelDisplay[3] ?? 0,
            color: "var(--color-warn)",
            summary: "Tried to Pay",
          },
          {
            label: "Payment Confirmed",
            value: registeredConfirmed,
            color: "#31c48d",
            trailingNote: hasShellGap
              ? {
                  label: "Gross Rev.",
                  value: formatGbpPence(grossRevPence),
                  subValue: `incl. ${formatGbpPence(unregisteredRevPence)} shell gap`,
                  subValueColor: "#22a04b",
                }
              : { label: "Gross Rev.", value: `(${formatGbpPence(grossRevPence)})` },
            extraSegment: hasShellGap
              ? {
                  value: unregisteredPayments,
                  color: "#15803d",
                  tooltip: `Shell telemetry gap: ${unregisteredPayments} — payment_confirmed with no matching payment_succeeded (same play session id or same device within −2 min…+10 min). ${formatGbpPence(unregisteredRevPence)} — already in Gross Rev. Same grain as Paid session explorer. Click: Raw Events → payment_confirmed. Middle-click / Ctrl/Cmd+click: new tab.`,
                }
              : undefined,
          },
        ]}
        stepTooltip={(step, i) => {
          const pageviewRaw = mobilePageviewEvents ?? Math.round(funnelDisplay[2] ?? 0);
          const attemptedRaw = paymentAttemptedEvents ?? Math.round(funnelDisplay[3] ?? 0);
          const hints = [
            `Click: Interaction sessions tab (distinct interaction_session_id stints in scope). Avg. /day = ${stintCount} stints ÷ ${reportingDays} reporting day(s).`,
            "Click: Raw Events → popup_open (payment popup). Funnel count is distinct ISIDs (* one per interaction_session_id).",
            `Distinct live play sessions (created ≤30 min before open + play_session_payments row). Raw page_opened events: ${pageviewRaw}. Click: Raw Events → screen_interacted (mobile_payment page_opened).`,
            `Distinct live play sessions with express_checkout_pressed. Raw events: ${attemptedRaw}. Click: Raw Events → screen_interacted (mobile_payment express_checkout_pressed).`,
            hasShellGap
              ? `Click: Raw Events → payment_confirmed. ${Math.round(registeredConfirmed + unregisteredPayments)} play sessions (${Math.round(registeredConfirmed)} registered + ${Math.round(unregisteredPayments)} shell gap) — same total as Paid session explorer. Gross Rev. from confirmed amounts (platform).`
              : "Click: Raw Events → payment_confirmed. Bar = play sessions with payment (same as Paid session explorer). Gross Rev. from confirmed amounts (platform).",
          ];
          return `${step.label}: ${i === 4 && hasShellGap ? Math.round(registeredConfirmed + unregisteredPayments) : Math.round(step.value)} — ${hints[i] ?? "Click: open Raw Events."} Middle-click / Ctrl/Cmd+click: new tab.`;
        }}
        onStepClick={(_, i, e) => {
          if (i === 0) {
            onOpenSessions(e);
          } else if (i === 1) {
            onOpenRawEvents(
              {
                eventName: "popup_open",
                propertiesContains: `"popup":"payment"`,
              },
              e,
            );
          } else if (i === 2) {
            onOpenRawEvents(
              {
                eventName: "screen_interacted",
                propertiesContains: `"screen_name":"mobile_payment"`,
                propertiesContains2: `"interaction_type":"page_opened"`,
              },
              e,
            );
          } else if (i === 3) {
            onOpenRawEvents(
              {
                eventName: "screen_interacted",
                propertiesContains: `"screen_name":"mobile_payment"`,
                propertiesContains2: `"interaction_type":"express_checkout_pressed"`,
              },
              e,
            );
          } else {
            onOpenRawEvents({ eventName: "payment_confirmed" }, e);
          }
        }}
        onExtraSegmentClick={(_, __, e) => {
          onOpenRawEvents({ eventName: "payment_confirmed" }, e);
        }}
      />
    </ChartCard>
  );
}
